import type { Product } from '@esf/shared';

/**
 * Serialised seat instances installed on operator fleets.
 * Generated from BoM JSONs. The showcase public passport is the first VantageNOVA suite (DPP-NOVA-001).
 */
export const demoProducts: Product[] = [
  {
    id: "PROD-NOVA-001",
    organisationId: "ORG-003",
    passportId: "DPP-NOVA-001",
    name: "VantageNOVA — next-generation Delta One Suite · N501DN · Suite 1A",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagenova.delta_a350_1000",
    serialNumber: "TAS-NOVA-001",
    gtin: "5012345010013",
    location: {
      lat: 33.6407,
      lng: -84.4277,
      address: "Aircraft N501DN (A350-1000)",
      city: "Atlanta, GA"
    },
    installationSite: "A350-1000 cabin · Suite 1A",
    installationDate: "2027-03-12",
    status: "ACTIVE",
    condition: "EXCELLENT",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2027-03-12",
    description: "Serialised instance of VantageNOVA — next-generation Delta One Suite, installed on N501DN (A350-1000).",
    tags: [
      "aerospace",
      "lie-flat",
      "a350-1000",
      "nova"
    ],
    masterProductId: "MP-NOVA",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-NOVA-001"
    ],
    materialComposition: {
      totalWeightGrams: 85000,
      components: [
        {
          id: "MC-004",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 26520,
          percentageByWeight: 31.2,
          isRecycled: true,
          recycledContentPercent: 6,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 15810,
          percentageByWeight: 18.6,
          isRecycled: true,
          recycledContentPercent: 22,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-003",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 11220,
          percentageByWeight: 13.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 8500,
          percentageByWeight: 10.0,
          isRecycled: true,
          recycledContentPercent: 32,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 5568,
          percentageByWeight: 6.6,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 4420,
          percentageByWeight: 5.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 3825,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 3528,
          percentageByWeight: 4.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 2720,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 2040,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 850,
          percentageByWeight: 1.0,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 9.3,
      preConsumerRecycledPercent: 9.3,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, herringbone geometry)",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Constellium is category-dominant in aerospace rolled plate, no public Thompson programme tie."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "FACC AG",
            address: "Address withheld (supplier confidence: INFERRED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Outboard suite shell (composite)",
          materialProvided: [
            "Carbon-fibre prepreg (Toray T700/T800-class) over Nomex / aramid honeycomb core, OOA cure. ESTIMATED."
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "PENDING",
          notes: "Confidence: INFERRED. ESTIMATED. FACC is the strongest corporate-adjacency inference for VantageNOVA shells — AVIC Cabin Systems sister company and Tier-1 aerospace composites house. Patents reference 'thermoplastic composite skins on aluminium honeycomb cores' consistent with FACC's product line. No public confirmation."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate, with possible aluminium honeycomb sandwich panel (Hexcel CR III / ECA core — typical for seat pans, NOT HRH-10 which is sidewall core)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (per-suite, structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Aubert & Duval and ATI are category-dominant aerospace forgers."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane stack: comfort layer ~40 kg/m³, support layer ~60 kg/m³, aramid felt fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace is category-dominant; specific Thompson tie not publicly attested."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; equivalent to legacy Vonar 3"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance. Vendor unknown for Thompson NOVA."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest / arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR treatment per FAR 25.853"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Verified via Thompson's own sustainability page naming Muirhead. Delta One programme historically uses Muirhead."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xnova001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xnova001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2027-03-12T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2027-03-12T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-NOVA-002",
    organisationId: "ORG-003",
    passportId: "DPP-NOVA-002",
    name: "VantageNOVA — next-generation Delta One Suite · N501DN · Suite 2A",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagenova.delta_a350_1000",
    serialNumber: "TAS-NOVA-002",
    gtin: "5012345010013",
    location: {
      lat: 33.6407,
      lng: -84.4277,
      address: "Aircraft N501DN (A350-1000)",
      city: "Atlanta, GA"
    },
    installationSite: "A350-1000 cabin · Suite 2A",
    installationDate: "2027-03-12",
    status: "ACTIVE",
    condition: "EXCELLENT",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2027-03-12",
    description: "Serialised instance of VantageNOVA — next-generation Delta One Suite, installed on N501DN (A350-1000).",
    tags: [
      "aerospace",
      "lie-flat",
      "a350-1000",
      "nova"
    ],
    masterProductId: "MP-NOVA",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-NOVA-002"
    ],
    materialComposition: {
      totalWeightGrams: 85000,
      components: [
        {
          id: "MC-004",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 26520,
          percentageByWeight: 31.2,
          isRecycled: true,
          recycledContentPercent: 6,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 15810,
          percentageByWeight: 18.6,
          isRecycled: true,
          recycledContentPercent: 22,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-003",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 11220,
          percentageByWeight: 13.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 8500,
          percentageByWeight: 10.0,
          isRecycled: true,
          recycledContentPercent: 32,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 5568,
          percentageByWeight: 6.6,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 4420,
          percentageByWeight: 5.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 3825,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 3528,
          percentageByWeight: 4.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 2720,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 2040,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 850,
          percentageByWeight: 1.0,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 9.3,
      preConsumerRecycledPercent: 9.3,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, herringbone geometry)",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Constellium is category-dominant in aerospace rolled plate, no public Thompson programme tie."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "FACC AG",
            address: "Address withheld (supplier confidence: INFERRED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Outboard suite shell (composite)",
          materialProvided: [
            "Carbon-fibre prepreg (Toray T700/T800-class) over Nomex / aramid honeycomb core, OOA cure. ESTIMATED."
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "PENDING",
          notes: "Confidence: INFERRED. ESTIMATED. FACC is the strongest corporate-adjacency inference for VantageNOVA shells — AVIC Cabin Systems sister company and Tier-1 aerospace composites house. Patents reference 'thermoplastic composite skins on aluminium honeycomb cores' consistent with FACC's product line. No public confirmation."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate, with possible aluminium honeycomb sandwich panel (Hexcel CR III / ECA core — typical for seat pans, NOT HRH-10 which is sidewall core)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (per-suite, structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Aubert & Duval and ATI are category-dominant aerospace forgers."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane stack: comfort layer ~40 kg/m³, support layer ~60 kg/m³, aramid felt fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace is category-dominant; specific Thompson tie not publicly attested."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; equivalent to legacy Vonar 3"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance. Vendor unknown for Thompson NOVA."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest / arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR treatment per FAR 25.853"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Verified via Thompson's own sustainability page naming Muirhead. Delta One programme historically uses Muirhead."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xnova002cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xnova002cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2027-03-12T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2027-03-12T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-NOVA-003",
    organisationId: "ORG-003",
    passportId: "DPP-NOVA-003",
    name: "VantageNOVA — next-generation Delta One Suite · N501DN · Suite 11K",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagenova.delta_a350_1000",
    serialNumber: "TAS-NOVA-003",
    gtin: "5012345010013",
    location: {
      lat: 33.6407,
      lng: -84.4277,
      address: "Aircraft N501DN (A350-1000)",
      city: "Atlanta, GA"
    },
    installationSite: "A350-1000 cabin · Suite 11K",
    installationDate: "2027-03-12",
    status: "ACTIVE",
    condition: "EXCELLENT",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2027-03-12",
    description: "Serialised instance of VantageNOVA — next-generation Delta One Suite, installed on N501DN (A350-1000).",
    tags: [
      "aerospace",
      "lie-flat",
      "a350-1000",
      "nova"
    ],
    masterProductId: "MP-NOVA",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-NOVA-003"
    ],
    materialComposition: {
      totalWeightGrams: 85000,
      components: [
        {
          id: "MC-004",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 26520,
          percentageByWeight: 31.2,
          isRecycled: true,
          recycledContentPercent: 6,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 15810,
          percentageByWeight: 18.6,
          isRecycled: true,
          recycledContentPercent: 22,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-003",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 11220,
          percentageByWeight: 13.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 8500,
          percentageByWeight: 10.0,
          isRecycled: true,
          recycledContentPercent: 32,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 5568,
          percentageByWeight: 6.6,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 4420,
          percentageByWeight: 5.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 3825,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 3528,
          percentageByWeight: 4.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 2720,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 2040,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 850,
          percentageByWeight: 1.0,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 9.3,
      preConsumerRecycledPercent: 9.3,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, herringbone geometry)",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Constellium is category-dominant in aerospace rolled plate, no public Thompson programme tie."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "FACC AG",
            address: "Address withheld (supplier confidence: INFERRED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Outboard suite shell (composite)",
          materialProvided: [
            "Carbon-fibre prepreg (Toray T700/T800-class) over Nomex / aramid honeycomb core, OOA cure. ESTIMATED."
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "PENDING",
          notes: "Confidence: INFERRED. ESTIMATED. FACC is the strongest corporate-adjacency inference for VantageNOVA shells — AVIC Cabin Systems sister company and Tier-1 aerospace composites house. Patents reference 'thermoplastic composite skins on aluminium honeycomb cores' consistent with FACC's product line. No public confirmation."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate, with possible aluminium honeycomb sandwich panel (Hexcel CR III / ECA core — typical for seat pans, NOT HRH-10 which is sidewall core)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (per-suite, structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Aubert & Duval and ATI are category-dominant aerospace forgers."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane stack: comfort layer ~40 kg/m³, support layer ~60 kg/m³, aramid felt fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace is category-dominant; specific Thompson tie not publicly attested."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; equivalent to legacy Vonar 3"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance. Vendor unknown for Thompson NOVA."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest / arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR treatment per FAR 25.853"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Verified via Thompson's own sustainability page naming Muirhead. Delta One programme historically uses Muirhead."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xnova003cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xnova003cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2027-03-12T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2027-03-12T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-NOVA-004",
    organisationId: "ORG-003",
    passportId: "DPP-NOVA-004",
    name: "VantageNOVA — next-generation Delta One Suite · N502DN · Suite 1A",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagenova.delta_a350_1000",
    serialNumber: "TAS-NOVA-004",
    gtin: "5012345010013",
    location: {
      lat: 33.6407,
      lng: -84.4277,
      address: "Aircraft N502DN (A350-1000)",
      city: "Atlanta, GA"
    },
    installationSite: "A350-1000 cabin · Suite 1A",
    installationDate: "2027-05-04",
    status: "ACTIVE",
    condition: "GOOD",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2027-05-04",
    description: "Serialised instance of VantageNOVA — next-generation Delta One Suite, installed on N502DN (A350-1000).",
    tags: [
      "aerospace",
      "lie-flat",
      "a350-1000",
      "nova"
    ],
    masterProductId: "MP-NOVA",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-NOVA-004"
    ],
    materialComposition: {
      totalWeightGrams: 85000,
      components: [
        {
          id: "MC-004",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 26520,
          percentageByWeight: 31.2,
          isRecycled: true,
          recycledContentPercent: 6,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 15810,
          percentageByWeight: 18.6,
          isRecycled: true,
          recycledContentPercent: 22,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-003",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 11220,
          percentageByWeight: 13.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 8500,
          percentageByWeight: 10.0,
          isRecycled: true,
          recycledContentPercent: 32,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 5568,
          percentageByWeight: 6.6,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 4420,
          percentageByWeight: 5.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 3825,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 3528,
          percentageByWeight: 4.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 2720,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 2040,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 850,
          percentageByWeight: 1.0,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 9.3,
      preConsumerRecycledPercent: 9.3,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, herringbone geometry)",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Constellium is category-dominant in aerospace rolled plate, no public Thompson programme tie."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "FACC AG",
            address: "Address withheld (supplier confidence: INFERRED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Outboard suite shell (composite)",
          materialProvided: [
            "Carbon-fibre prepreg (Toray T700/T800-class) over Nomex / aramid honeycomb core, OOA cure. ESTIMATED."
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "PENDING",
          notes: "Confidence: INFERRED. ESTIMATED. FACC is the strongest corporate-adjacency inference for VantageNOVA shells — AVIC Cabin Systems sister company and Tier-1 aerospace composites house. Patents reference 'thermoplastic composite skins on aluminium honeycomb cores' consistent with FACC's product line. No public confirmation."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate, with possible aluminium honeycomb sandwich panel (Hexcel CR III / ECA core — typical for seat pans, NOT HRH-10 which is sidewall core)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (per-suite, structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Aubert & Duval and ATI are category-dominant aerospace forgers."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane stack: comfort layer ~40 kg/m³, support layer ~60 kg/m³, aramid felt fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace is category-dominant; specific Thompson tie not publicly attested."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; equivalent to legacy Vonar 3"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance. Vendor unknown for Thompson NOVA."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest / arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR treatment per FAR 25.853"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Verified via Thompson's own sustainability page naming Muirhead. Delta One programme historically uses Muirhead."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xnova004cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xnova004cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2027-05-04T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2027-05-04T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-XL-001",
    organisationId: "ORG-003",
    passportId: "DPP-XL-001",
    name: "VantageXL — Delta One Suite · N502DC · Suite 1A",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagexl.delta_one_suite",
    serialNumber: "TAS-XL-001",
    gtin: "5012345010020",
    location: {
      lat: 33.6407,
      lng: -84.4277,
      address: "Aircraft N502DC (A350-900)",
      city: "Atlanta, GA"
    },
    installationSite: "A350-900 cabin · Suite 1A",
    installationDate: "2018-06-22",
    status: "ACTIVE",
    condition: "GOOD",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2018-06-22",
    description: "Serialised instance of VantageXL — Delta One Suite, installed on N502DC (A350-900).",
    tags: [
      "aerospace",
      "lie-flat",
      "a350-900",
      "xl"
    ],
    masterProductId: "MP-XL",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-XL-001"
    ],
    materialComposition: {
      totalWeightGrams: 95000,
      components: [
        {
          id: "MC-003",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 36242,
          percentageByWeight: 38.1,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-004",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 11400,
          percentageByWeight: 12.0,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 9310,
          percentageByWeight: 9.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 7885,
          percentageByWeight: 8.3,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 6840,
          percentageByWeight: 7.2,
          isRecycled: true,
          recycledContentPercent: 28,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 6460,
          percentageByWeight: 6.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 6175,
          percentageByWeight: 6.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 3088,
          percentageByWeight: 3.3,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 3040,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 2280,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 2280,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 2.0,
      preConsumerRecycledPercent: 2.0,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent of TSO-C127c"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia loads (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic test + Head Injury Criterion (HIC) ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses — top-level seat rule"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage, tables — 9g forward)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during taxi/take-off/landing (interlock)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "12-second vertical Bunsen burn — all interior materials"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH)",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Constellium category-dominant in aerospace rolled plate; not publicly attested for Thompson programmes."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader fittings (track-attach feet)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forging, AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Category-dominant forgers; no public Thompson programme tie."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Lisi Aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins, NAS bolts",
          materialProvided: [
            "15-5PH, A286, Ti alloys to NAS1670 / MS21042"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard suppliers; no Thompson-specific public attestation."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "WITHHELD",
            address: "Address withheld (supplier confidence: WITHHELD)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Composite shell / pan (where used)",
          materialProvided: [
            "CFRP layup over Hexcel HRH-10 Nomex core (typical prepreg: Toray T700/T800 or Solvay CYCOM 977-2 / 5320-1)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: WITHHELD. GAP: Composite shell manufacturer for production seats is not publicly disclosed. FACC AG is corporate-adjacency inference (AVIC sister) with no confirmed Thompson programme tie. DPP would fill this leaf."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Replin by Hainsworth — Steadfast Collection",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Woven wool / hemp / alpaca dress cover (undyed)",
          materialProvided: [
            "Undyed natural fibre blend (wool / hemp / alpaca)"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Publicly named on Thompson's sustainability page. Strong chain-of-custody story on the natural fibre."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Leather (headrests, arm caps)",
          materialProvided: [
            "Full-grain aviation leather, Scottish hides"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Named on Thompson's sustainability page. State functional unit explicitly when quoting the 8 kg figure."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "YKK Aerospace",
            address: "Address withheld (supplier confidence: INFERRED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Thread, Velcro, closures",
          materialProvided: [
            "Nomex or PTFE-coated polyester thread; aerospace-grade YKK zippers; FR Velcro"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "PENDING",
          notes: "Confidence: INFERRED. Category-typical suppliers; no Thompson-specific evidence."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Crane Aerospace & Electronics mcX G3",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Recline / leg-rest actuator",
          materialProvided: [
            "Brushless DC linear actuator, integrated motor drive + CAN bus"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Crane mcX G3 is the dominant business-class actuator. Crane's public material names Adient (Ascent) as a seat-OEM customer, not Thompson — do not over-claim."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xxl001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xxl001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2018-06-22T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2018-06-22T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-XL-002",
    organisationId: "ORG-003",
    passportId: "DPP-XL-002",
    name: "VantageXL — Delta One Suite · N502DC · Suite 2A",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagexl.delta_one_suite",
    serialNumber: "TAS-XL-002",
    gtin: "5012345010020",
    location: {
      lat: 33.6407,
      lng: -84.4277,
      address: "Aircraft N502DC (A350-900)",
      city: "Atlanta, GA"
    },
    installationSite: "A350-900 cabin · Suite 2A",
    installationDate: "2018-06-22",
    status: "MAINTENANCE",
    condition: "GOOD",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2026-08-01",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2018-06-22",
    description: "Serialised instance of VantageXL — Delta One Suite, installed on N502DC (A350-900).",
    tags: [
      "aerospace",
      "lie-flat",
      "a350-900",
      "xl"
    ],
    masterProductId: "MP-XL",
    masterProductVersion: 1,
    iotDeviceIds: [],
    materialComposition: {
      totalWeightGrams: 95000,
      components: [
        {
          id: "MC-003",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 36242,
          percentageByWeight: 38.1,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-004",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 11400,
          percentageByWeight: 12.0,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 9310,
          percentageByWeight: 9.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 7885,
          percentageByWeight: 8.3,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 6840,
          percentageByWeight: 7.2,
          isRecycled: true,
          recycledContentPercent: 28,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 6460,
          percentageByWeight: 6.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 6175,
          percentageByWeight: 6.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 3088,
          percentageByWeight: 3.3,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 3040,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 2280,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 2280,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 2.0,
      preConsumerRecycledPercent: 2.0,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent of TSO-C127c"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia loads (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic test + Head Injury Criterion (HIC) ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses — top-level seat rule"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage, tables — 9g forward)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during taxi/take-off/landing (interlock)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "12-second vertical Bunsen burn — all interior materials"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH)",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Constellium category-dominant in aerospace rolled plate; not publicly attested for Thompson programmes."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader fittings (track-attach feet)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forging, AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Category-dominant forgers; no public Thompson programme tie."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Lisi Aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins, NAS bolts",
          materialProvided: [
            "15-5PH, A286, Ti alloys to NAS1670 / MS21042"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard suppliers; no Thompson-specific public attestation."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "WITHHELD",
            address: "Address withheld (supplier confidence: WITHHELD)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Composite shell / pan (where used)",
          materialProvided: [
            "CFRP layup over Hexcel HRH-10 Nomex core (typical prepreg: Toray T700/T800 or Solvay CYCOM 977-2 / 5320-1)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: WITHHELD. GAP: Composite shell manufacturer for production seats is not publicly disclosed. FACC AG is corporate-adjacency inference (AVIC sister) with no confirmed Thompson programme tie. DPP would fill this leaf."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Replin by Hainsworth — Steadfast Collection",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Woven wool / hemp / alpaca dress cover (undyed)",
          materialProvided: [
            "Undyed natural fibre blend (wool / hemp / alpaca)"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Publicly named on Thompson's sustainability page. Strong chain-of-custody story on the natural fibre."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Leather (headrests, arm caps)",
          materialProvided: [
            "Full-grain aviation leather, Scottish hides"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Named on Thompson's sustainability page. State functional unit explicitly when quoting the 8 kg figure."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "YKK Aerospace",
            address: "Address withheld (supplier confidence: INFERRED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Thread, Velcro, closures",
          materialProvided: [
            "Nomex or PTFE-coated polyester thread; aerospace-grade YKK zippers; FR Velcro"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "PENDING",
          notes: "Confidence: INFERRED. Category-typical suppliers; no Thompson-specific evidence."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Crane Aerospace & Electronics mcX G3",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Recline / leg-rest actuator",
          materialProvided: [
            "Brushless DC linear actuator, integrated motor drive + CAN bus"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Crane mcX G3 is the dominant business-class actuator. Crane's public material names Adient (Ascent) as a seat-OEM customer, not Thompson — do not over-claim."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xxl002cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xxl002cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2018-06-22T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2018-06-22T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-XL-003",
    organisationId: "ORG-003",
    passportId: "DPP-XL-003",
    name: "VantageXL — Delta One Suite · N506DN · Suite 1A",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagexl.delta_one_suite",
    serialNumber: "TAS-XL-003",
    gtin: "5012345010020",
    location: {
      lat: 33.6407,
      lng: -84.4277,
      address: "Aircraft N506DN (A350-900)",
      city: "Atlanta, GA"
    },
    installationSite: "A350-900 cabin · Suite 1A",
    installationDate: "2019-11-05",
    status: "ACTIVE",
    condition: "FAIR",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2019-11-05",
    description: "Serialised instance of VantageXL — Delta One Suite, installed on N506DN (A350-900).",
    tags: [
      "aerospace",
      "lie-flat",
      "a350-900",
      "xl"
    ],
    masterProductId: "MP-XL",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-XL-003"
    ],
    materialComposition: {
      totalWeightGrams: 95000,
      components: [
        {
          id: "MC-003",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 36242,
          percentageByWeight: 38.1,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-004",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 11400,
          percentageByWeight: 12.0,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 9310,
          percentageByWeight: 9.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 7885,
          percentageByWeight: 8.3,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 6840,
          percentageByWeight: 7.2,
          isRecycled: true,
          recycledContentPercent: 28,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 6460,
          percentageByWeight: 6.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 6175,
          percentageByWeight: 6.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 3088,
          percentageByWeight: 3.3,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 3040,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 2280,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 2280,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 2.0,
      preConsumerRecycledPercent: 2.0,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent of TSO-C127c"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia loads (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic test + Head Injury Criterion (HIC) ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses — top-level seat rule"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage, tables — 9g forward)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during taxi/take-off/landing (interlock)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "12-second vertical Bunsen burn — all interior materials"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH)",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Constellium category-dominant in aerospace rolled plate; not publicly attested for Thompson programmes."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader fittings (track-attach feet)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forging, AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Category-dominant forgers; no public Thompson programme tie."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Lisi Aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins, NAS bolts",
          materialProvided: [
            "15-5PH, A286, Ti alloys to NAS1670 / MS21042"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard suppliers; no Thompson-specific public attestation."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "WITHHELD",
            address: "Address withheld (supplier confidence: WITHHELD)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Composite shell / pan (where used)",
          materialProvided: [
            "CFRP layup over Hexcel HRH-10 Nomex core (typical prepreg: Toray T700/T800 or Solvay CYCOM 977-2 / 5320-1)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: WITHHELD. GAP: Composite shell manufacturer for production seats is not publicly disclosed. FACC AG is corporate-adjacency inference (AVIC sister) with no confirmed Thompson programme tie. DPP would fill this leaf."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Replin by Hainsworth — Steadfast Collection",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Woven wool / hemp / alpaca dress cover (undyed)",
          materialProvided: [
            "Undyed natural fibre blend (wool / hemp / alpaca)"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Publicly named on Thompson's sustainability page. Strong chain-of-custody story on the natural fibre."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Leather (headrests, arm caps)",
          materialProvided: [
            "Full-grain aviation leather, Scottish hides"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Named on Thompson's sustainability page. State functional unit explicitly when quoting the 8 kg figure."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "YKK Aerospace",
            address: "Address withheld (supplier confidence: INFERRED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Thread, Velcro, closures",
          materialProvided: [
            "Nomex or PTFE-coated polyester thread; aerospace-grade YKK zippers; FR Velcro"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "PENDING",
          notes: "Confidence: INFERRED. Category-typical suppliers; no Thompson-specific evidence."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Crane Aerospace & Electronics mcX G3",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Recline / leg-rest actuator",
          materialProvided: [
            "Brushless DC linear actuator, integrated motor drive + CAN bus"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Crane mcX G3 is the dominant business-class actuator. Crane's public material names Adient (Ascent) as a seat-OEM customer, not Thompson — do not over-claim."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xxl003cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xxl003cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2019-11-05T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2019-11-05T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-SOLO-001",
    organisationId: "ORG-003",
    passportId: "DPP-SOLO-001",
    name: "VantageSOLO — Mint Suite (JetBlue) · N2061J · Suite 1A",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagesolo.jetblue_mint",
    serialNumber: "TAS-SOLO-001",
    gtin: "5012345010037",
    location: {
      lat: 40.6413,
      lng: -73.7781,
      address: "Aircraft N2061J (A321LR)",
      city: "Atlanta, GA"
    },
    installationSite: "A321LR cabin · Suite 1A",
    installationDate: "2021-04-18",
    status: "ACTIVE",
    condition: "GOOD",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2021-04-18",
    description: "Serialised instance of VantageSOLO — Mint Suite (JetBlue), installed on N2061J (A321LR).",
    tags: [
      "aerospace",
      "lie-flat",
      "a321lr",
      "solo"
    ],
    masterProductId: "MP-SOLO",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-SOLO-001"
    ],
    materialComposition: {
      totalWeightGrams: 70000,
      components: [
        {
          id: "MC-003",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 26460,
          percentageByWeight: 37.8,
          isRecycled: true,
          recycledContentPercent: 7,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 15960,
          percentageByWeight: 22.8,
          isRecycled: true,
          recycledContentPercent: 18,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 7840,
          percentageByWeight: 11.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-004",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 4340,
          percentageByWeight: 6.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 3150,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 3080,
          percentageByWeight: 4.4,
          isRecycled: true,
          recycledContentPercent: 73,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 2660,
          percentageByWeight: 3.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 2240,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 2240,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 1680,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 350,
          percentageByWeight: 0.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 9.8,
      preConsumerRecycledPercent: 9.8,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, staggered (original Vantage DNA))",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Category-dominant supplier for aerospace rolled plate."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate; aluminium honeycomb sandwich (Hexcel ECA / CR III)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane: comfort ~40 kg/m³, support ~60 kg/m³, aramid fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace category-dominant."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; legacy Vonar 3 equivalent"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest, arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR-treated"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED — Thompson's sustainability page names Muirhead."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Replin by Hainsworth",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — woven wool / hemp",
          materialProvided: [
            "Undyed wool / alpaca / hemp blend, FR scrim backing"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED. Airline CMF dependent."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xsolo001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xsolo001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2021-04-18T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2021-04-18T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-SOLO-002",
    organisationId: "ORG-003",
    passportId: "DPP-SOLO-002",
    name: "VantageSOLO — Mint Suite (JetBlue) · N2061J · Suite 2D",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagesolo.jetblue_mint",
    serialNumber: "TAS-SOLO-002",
    gtin: "5012345010037",
    location: {
      lat: 40.6413,
      lng: -73.7781,
      address: "Aircraft N2061J (A321LR)",
      city: "Atlanta, GA"
    },
    installationSite: "A321LR cabin · Suite 2D",
    installationDate: "2021-04-18",
    status: "ACTIVE",
    condition: "GOOD",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2021-04-18",
    description: "Serialised instance of VantageSOLO — Mint Suite (JetBlue), installed on N2061J (A321LR).",
    tags: [
      "aerospace",
      "lie-flat",
      "a321lr",
      "solo"
    ],
    masterProductId: "MP-SOLO",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-SOLO-002"
    ],
    materialComposition: {
      totalWeightGrams: 70000,
      components: [
        {
          id: "MC-003",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 26460,
          percentageByWeight: 37.8,
          isRecycled: true,
          recycledContentPercent: 7,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 15960,
          percentageByWeight: 22.8,
          isRecycled: true,
          recycledContentPercent: 18,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 7840,
          percentageByWeight: 11.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-004",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 4340,
          percentageByWeight: 6.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 3150,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 3080,
          percentageByWeight: 4.4,
          isRecycled: true,
          recycledContentPercent: 73,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 2660,
          percentageByWeight: 3.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 2240,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 2240,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 1680,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 350,
          percentageByWeight: 0.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 9.8,
      preConsumerRecycledPercent: 9.8,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, staggered (original Vantage DNA))",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Category-dominant supplier for aerospace rolled plate."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate; aluminium honeycomb sandwich (Hexcel ECA / CR III)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane: comfort ~40 kg/m³, support ~60 kg/m³, aramid fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace category-dominant."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; legacy Vonar 3 equivalent"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest, arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR-treated"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED — Thompson's sustainability page names Muirhead."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Replin by Hainsworth",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — woven wool / hemp",
          materialProvided: [
            "Undyed wool / alpaca / hemp blend, FR scrim backing"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED. Airline CMF dependent."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xsolo002cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xsolo002cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2021-04-18T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2021-04-18T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-XLFIRST-001",
    organisationId: "ORG-003",
    passportId: "DPP-XLFIRST-001",
    name: "VantageXL First — Virgin Atlantic Retreat Suite · G-VLUX · Suite 1A",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagexl_first.virgin_atlantic",
    serialNumber: "TAS-XLFIRST-001",
    gtin: "5012345010044",
    location: {
      lat: 51.47,
      lng: -0.4543,
      address: "Aircraft G-VLUX (A330-900)",
      city: "Atlanta, GA"
    },
    installationSite: "A330-900 cabin · Suite 1A",
    installationDate: "2023-09-30",
    status: "ACTIVE",
    condition: "EXCELLENT",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2023-09-30",
    description: "Serialised instance of VantageXL First — Virgin Atlantic Retreat Suite, installed on G-VLUX (A330-900).",
    tags: [
      "aerospace",
      "premium",
      "a330-900",
      "xlfirst"
    ],
    masterProductId: "MP-XLFIRST",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-XLFIRST-001"
    ],
    materialComposition: {
      totalWeightGrams: 95000,
      components: [
        {
          id: "MC-003",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 36480,
          percentageByWeight: 38.4,
          isRecycled: true,
          recycledContentPercent: 7,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 22230,
          percentageByWeight: 23.4,
          isRecycled: true,
          recycledContentPercent: 17,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 10640,
          percentageByWeight: 11.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-004",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 5890,
          percentageByWeight: 6.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 4275,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 3610,
          percentageByWeight: 3.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 3040,
          percentageByWeight: 3.2,
          isRecycled: true,
          recycledContentPercent: 100,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 3040,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 3040,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 2280,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 475,
          percentageByWeight: 0.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 9.8,
      preConsumerRecycledPercent: 9.8,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, staggered with widened footwell (XL/XL+ geometry))",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Category-dominant supplier for aerospace rolled plate."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate; aluminium honeycomb sandwich (Hexcel ECA / CR III)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane: comfort ~40 kg/m³, support ~60 kg/m³, aramid fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace category-dominant."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; legacy Vonar 3 equivalent"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest, arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR-treated"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED — Thompson's sustainability page names Muirhead."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Replin by Hainsworth",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — woven wool / hemp",
          materialProvided: [
            "Undyed wool / alpaca / hemp blend, FR scrim backing"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED. Airline CMF dependent."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xxlfirst001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xxlfirst001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2023-09-30T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2023-09-30T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-XLPLUS-001",
    organisationId: "ORG-003",
    passportId: "DPP-XLPLUS-001",
    name: "VantageXL+ — A350 / B787 optimised · B-30A1 · Suite 1A",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagexlplus.china_southern",
    serialNumber: "TAS-XLPLUS-001",
    gtin: "5012345010051",
    location: {
      lat: 23.3924,
      lng: 113.2988,
      address: "Aircraft B-30A1 (A350-900)",
      city: "Atlanta, GA"
    },
    installationSite: "A350-900 cabin · Suite 1A",
    installationDate: "2024-12-14",
    status: "ACTIVE",
    condition: "EXCELLENT",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2024-12-14",
    description: "Serialised instance of VantageXL+ — A350 / B787 optimised, installed on B-30A1 (A350-900).",
    tags: [
      "aerospace",
      "lie-flat",
      "a350-900",
      "xlplus"
    ],
    masterProductId: "MP-XLPLUS",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-XLPLUS-001"
    ],
    materialComposition: {
      totalWeightGrams: 80000,
      components: [
        {
          id: "MC-003",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 30160,
          percentageByWeight: 37.7,
          isRecycled: true,
          recycledContentPercent: 7,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 18720,
          percentageByWeight: 23.4,
          isRecycled: true,
          recycledContentPercent: 17,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 8960,
          percentageByWeight: 11.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-004",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 5240,
          percentageByWeight: 6.6,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 3600,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 3320,
          percentageByWeight: 4.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 2560,
          percentageByWeight: 3.2,
          isRecycled: true,
          recycledContentPercent: 100,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 2560,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 2560,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 1920,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 400,
          percentageByWeight: 0.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 9.8,
      preConsumerRecycledPercent: 9.8,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, staggered with widened footwell (XL/XL+ geometry))",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Category-dominant supplier for aerospace rolled plate."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate; aluminium honeycomb sandwich (Hexcel ECA / CR III)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane: comfort ~40 kg/m³, support ~60 kg/m³, aramid fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace category-dominant."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; legacy Vonar 3 equivalent"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest, arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR-treated"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED — Thompson's sustainability page names Muirhead."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Replin by Hainsworth",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — woven wool / hemp",
          materialProvided: [
            "Undyed wool / alpaca / hemp blend, FR scrim backing"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED. Airline CMF dependent."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xxlplus001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xxlplus001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2024-12-14T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2024-12-14T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-XLPLUS-002",
    organisationId: "ORG-003",
    passportId: "DPP-XLPLUS-002",
    name: "VantageXL+ — A350 / B787 optimised · B-30A1 · Suite 2K",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagexlplus.china_southern",
    serialNumber: "TAS-XLPLUS-002",
    gtin: "5012345010051",
    location: {
      lat: 23.3924,
      lng: 113.2988,
      address: "Aircraft B-30A1 (A350-900)",
      city: "Atlanta, GA"
    },
    installationSite: "A350-900 cabin · Suite 2K",
    installationDate: "2024-12-14",
    status: "ACTIVE",
    condition: "EXCELLENT",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2024-12-14",
    description: "Serialised instance of VantageXL+ — A350 / B787 optimised, installed on B-30A1 (A350-900).",
    tags: [
      "aerospace",
      "lie-flat",
      "a350-900",
      "xlplus"
    ],
    masterProductId: "MP-XLPLUS",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-XLPLUS-002"
    ],
    materialComposition: {
      totalWeightGrams: 80000,
      components: [
        {
          id: "MC-003",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 30160,
          percentageByWeight: 37.7,
          isRecycled: true,
          recycledContentPercent: 7,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 18720,
          percentageByWeight: 23.4,
          isRecycled: true,
          recycledContentPercent: 17,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 8960,
          percentageByWeight: 11.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-004",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 5240,
          percentageByWeight: 6.6,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 3600,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 3320,
          percentageByWeight: 4.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 2560,
          percentageByWeight: 3.2,
          isRecycled: true,
          recycledContentPercent: 100,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 2560,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 2560,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 1920,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 400,
          percentageByWeight: 0.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 9.8,
      preConsumerRecycledPercent: 9.8,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, staggered with widened footwell (XL/XL+ geometry))",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Category-dominant supplier for aerospace rolled plate."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate; aluminium honeycomb sandwich (Hexcel ECA / CR III)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane: comfort ~40 kg/m³, support ~60 kg/m³, aramid fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace category-dominant."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; legacy Vonar 3 equivalent"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest, arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR-treated"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED — Thompson's sustainability page names Muirhead."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Replin by Hainsworth",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — woven wool / hemp",
          materialProvided: [
            "Undyed wool / alpaca / hemp blend, FR scrim backing"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED. Airline CMF dependent."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xxlplus002cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xxlplus002cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2024-12-14T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2024-12-14T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-XLPLUSFIRST-001",
    organisationId: "ORG-002",
    passportId: "DPP-XLPLUSFIRST-001",
    name: "VantageXL+ First — widest suite yet · DEMO-1 · Suite Mockup 1A",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagexlplus_first.aix2026",
    serialNumber: "TAS-XLPLUSFIRST-001",
    gtin: "5012345010068",
    location: {
      lat: 54.4214,
      lng: -6.4456,
      address: "Aircraft DEMO-1 (A350 mockup)",
      city: "Portadown"
    },
    installationSite: "A350 mockup cabin · Suite Mockup 1A",
    installationDate: "2026-03-01",
    status: "PENDING_INSTALLATION",
    condition: "EXCELLENT",
    nextMaintenanceDate: "2026-08-01",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2026-03-01",
    description: "Serialised instance of VantageXL+ First — widest suite yet, installed on DEMO-1 (A350 mockup).",
    tags: [
      "aerospace",
      "lie-flat",
      "a350 mockup",
      "xlplusfirst"
    ],
    masterProductId: "MP-XLPLUSFIRST",
    masterProductVersion: 1,
    iotDeviceIds: [],
    materialComposition: {
      totalWeightGrams: 100000,
      components: [
        {
          id: "MC-004",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 38400,
          percentageByWeight: 38.4,
          isRecycled: true,
          recycledContentPercent: 7,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 16600,
          percentageByWeight: 16.6,
          isRecycled: true,
          recycledContentPercent: 25,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-003",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 11200,
          percentageByWeight: 11.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 10000,
          percentageByWeight: 10.0,
          isRecycled: true,
          recycledContentPercent: 32,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 6200,
          percentageByWeight: 6.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 4500,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 3800,
          percentageByWeight: 3.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 3200,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 3200,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 2400,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 500,
          percentageByWeight: 0.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 9.8,
      preConsumerRecycledPercent: 9.8,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, first-class-density widened (First-variant geometry))",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Category-dominant supplier for aerospace rolled plate."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate; aluminium honeycomb sandwich (Hexcel ECA / CR III)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "FACC AG",
            address: "Address withheld (supplier confidence: INFERRED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Outboard suite shell (composite)",
          materialProvided: [
            "Carbon-fibre prepreg (Toray T700/T800-class) over Nomex / aramid honeycomb core, OOA cure"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "PENDING",
          notes: "Confidence: INFERRED. ESTIMATED. FACC is the strongest corporate-adjacency inference."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane: comfort ~40 kg/m³, support ~60 kg/m³, aramid fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace category-dominant."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; legacy Vonar 3 equivalent"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest, arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR-treated"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED — Thompson's sustainability page names Muirhead."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xxlplusfirst001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xxlplusfirst001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2026-03-01T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2026-03-01T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-NOVAFIRST-001",
    organisationId: "ORG-002",
    passportId: "DPP-NOVAFIRST-001",
    name: "VantageNOVA First — 'Star' shared-dining first-class · DEMO-2 · Suite Star 1",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantagenovafirst.aix2025",
    serialNumber: "TAS-NOVAFIRST-001",
    gtin: "5012345010075",
    location: {
      lat: 54.4214,
      lng: -6.4456,
      address: "Aircraft DEMO-2 (A350 mockup)",
      city: "Portadown"
    },
    installationSite: "A350 mockup cabin · Suite Star 1",
    installationDate: "2026-02-12",
    status: "PENDING_INSTALLATION",
    condition: "EXCELLENT",
    nextMaintenanceDate: "2026-08-01",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2026-02-12",
    description: "Serialised instance of VantageNOVA First — 'Star' shared-dining first-class, installed on DEMO-2 (A350 mockup).",
    tags: [
      "aerospace",
      "premium",
      "a350 mockup",
      "novafirst"
    ],
    masterProductId: "MP-NOVAFIRST",
    masterProductVersion: 1,
    iotDeviceIds: [],
    materialComposition: {
      totalWeightGrams: 100000,
      components: [
        {
          id: "MC-004",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 38400,
          percentageByWeight: 38.4,
          isRecycled: true,
          recycledContentPercent: 7,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 16600,
          percentageByWeight: 16.6,
          isRecycled: true,
          recycledContentPercent: 25,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-003",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 11200,
          percentageByWeight: 11.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 10000,
          percentageByWeight: 10.0,
          isRecycled: true,
          recycledContentPercent: 32,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 6200,
          percentageByWeight: 6.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 4500,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 3800,
          percentageByWeight: 3.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 3200,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Silicone",
          materialType: "Silicone",
          weightGrams: 3200,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 2400,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 500,
          percentageByWeight: 0.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 9.8,
      preConsumerRecycledPercent: 9.8,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, reverse-herringbone (NOVA-derived))",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Category-dominant supplier for aerospace rolled plate."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate; aluminium honeycomb sandwich (Hexcel ECA / CR III)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "FACC AG",
            address: "Address withheld (supplier confidence: INFERRED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Outboard suite shell (composite)",
          materialProvided: [
            "Carbon-fibre prepreg (Toray T700/T800-class) over Nomex / aramid honeycomb core, OOA cure"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "PENDING",
          notes: "Confidence: INFERRED. ESTIMATED. FACC is the strongest corporate-adjacency inference."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane: comfort ~40 kg/m³, support ~60 kg/m³, aramid fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace category-dominant."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; legacy Vonar 3 equivalent"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest, arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR-treated"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED — Thompson's sustainability page names Muirhead."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xnovafirst001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xnovafirst001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2026-02-12T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2026-02-12T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-DUO-001",
    organisationId: "ORG-002",
    passportId: "DPP-DUO-001",
    name: "VantageDUO — paired angled 'zero-G' recliner · DEMO-3 · Suite Pair 1L/R",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantageduo.aix2022",
    serialNumber: "TAS-DUO-001",
    gtin: "5012345010082",
    location: {
      lat: 54.4214,
      lng: -6.4456,
      address: "Aircraft DEMO-3 (A321XLR mockup)",
      city: "Portadown"
    },
    installationSite: "A321XLR mockup cabin · Suite Pair 1L/R",
    installationDate: "2025-11-20",
    status: "PENDING_INSTALLATION",
    condition: "GOOD",
    nextMaintenanceDate: "2026-08-01",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785",
      "FAR/CS 25.789"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2025-11-20",
    description: "Serialised instance of VantageDUO — paired angled 'zero-G' recliner, installed on DEMO-3 (A321XLR mockup).",
    tags: [
      "aerospace",
      "lie-flat",
      "a321xlr mockup",
      "duo"
    ],
    masterProductId: "MP-DUO",
    masterProductVersion: 1,
    iotDeviceIds: [],
    materialComposition: {
      totalWeightGrams: 60000,
      components: [
        {
          id: "MC-004",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 18540,
          percentageByWeight: 30.9,
          isRecycled: true,
          recycledContentPercent: 8,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 9960,
          percentageByWeight: 16.6,
          isRecycled: true,
          recycledContentPercent: 25,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-003",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 6480,
          percentageByWeight: 10.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 5690,
          percentageByWeight: 9.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 4680,
          percentageByWeight: 7.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 3930,
          percentageByWeight: 6.6,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 3200,
          percentageByWeight: 5.3,
          isRecycled: true,
          recycledContentPercent: 100,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Polyurethane Foam",
          materialType: "Polyurethane Foam",
          weightGrams: 3200,
          percentageByWeight: 5.3,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 2880,
          percentageByWeight: 4.8,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 1440,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 11.9,
      preConsumerRecycledPercent: 11.9,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.813",
        certificateNumber: "FAR/CS 25.813",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency egress — privacy door must open during TTL (interlock-enforced)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, paired angled-towards-window (DUO 'zero-G' geometry — not lie-flat))",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Category-dominant supplier for aerospace rolled plate."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate; aluminium honeycomb sandwich (Hexcel ECA / CR III)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane: comfort ~40 kg/m³, support ~60 kg/m³, aramid fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace category-dominant."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; legacy Vonar 3 equivalent"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest, arm caps)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR-treated"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED — Thompson's sustainability page names Muirhead."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Replin by Hainsworth",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — woven wool / hemp",
          materialProvided: [
            "Undyed wool / alpaca / hemp blend, FR scrim backing"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. VERIFIED. Airline CMF dependent."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xduo001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xduo001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2025-11-20T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2025-11-20T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  },
  {
    id: "PROD-VANTAGE-001",
    organisationId: "ORG-002",
    passportId: "DPP-VANTAGE-001",
    name: "Vantage — staggered fully-flat business-class · SHOW-1 · Suite Heritage 1A",
    category: "seating",
    subcategory: "Aircraft Passenger Suite",
    model: "seat.vantage.showroom",
    serialNumber: "TAS-VANTAGE-001",
    gtin: "5012345010099",
    location: {
      lat: 54.4214,
      lng: -6.4456,
      address: "Aircraft SHOW-1 (Showroom)",
      city: "Portadown"
    },
    installationSite: "Showroom cabin · Suite Heritage 1A",
    installationDate: "2024-04-12",
    status: "ACTIVE",
    condition: "EXCELLENT",
    lastInspectionDate: "2026-04-20",
    nextMaintenanceDate: "2027-09-15",
    manufacturer: "Thompson Aero Seating Ltd",
    manufacturerCountry: "GB",
    certifications: [
      "TSO-C127c",
      "ETSO-C127c",
      "TSO-C39c",
      "FAR/CS 25.561",
      "FAR/CS 25.562",
      "FAR/CS 25.785"
    ],
    carbonFootprint: 1100.0,
    recyclabilityScore: 78,
    expectedLifespan: 15,
    warrantyEndDate: "2030-12-31",
    repairabilityScore: 7.5,
    productionDate: "2024-04-12",
    description: "Serialised instance of Vantage — staggered fully-flat business-class, installed on SHOW-1 (Showroom).",
    tags: [
      "aerospace",
      "lie-flat",
      "showroom",
      "vantage"
    ],
    masterProductId: "MP-VANTAGE",
    masterProductVersion: 1,
    iotDeviceIds: [
      "IOT-VANTAGE-001"
    ],
    materialComposition: {
      totalWeightGrams: 65000,
      components: [
        {
          id: "MC-001",
          name: "Metal - Aluminium",
          materialType: "Metal - Aluminium",
          weightGrams: 18590,
          percentageByWeight: 28.6,
          isRecycled: true,
          recycledContentPercent: 14,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-003",
          name: "Composite / Other",
          materialType: "Composite / Other",
          weightGrams: 17225,
          percentageByWeight: 26.5,
          isRecycled: true,
          recycledContentPercent: 20,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-002",
          name: "Metal - Titanium",
          materialType: "Metal - Titanium",
          weightGrams: 7280,
          percentageByWeight: 11.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-005",
          name: "Leather",
          materialType: "Leather",
          weightGrams: 6078,
          percentageByWeight: 9.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-004",
          name: "Aramid Honeycomb Core",
          materialType: "Aramid Honeycomb Core",
          weightGrams: 4258,
          percentageByWeight: 6.6,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-007",
          name: "Electronics Assembly",
          materialType: "Electronics Assembly",
          weightGrams: 2925,
          percentageByWeight: 4.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-008",
          name: "Thermoplastic",
          materialType: "Thermoplastic",
          weightGrams: 2275,
          percentageByWeight: 3.5,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-010",
          name: "Carbon Fibre Composite",
          materialType: "Carbon Fibre Composite",
          weightGrams: 2080,
          percentageByWeight: 3.2,
          isRecycled: true,
          recycledContentPercent: 100,
          isPreConsumerRecycled: true,
          originCountry: "GB"
        },
        {
          id: "MC-011",
          name: "Polyurethane Foam",
          materialType: "Polyurethane Foam",
          weightGrams: 2080,
          percentageByWeight: 3.2,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-006",
          name: "Wool Textile",
          materialType: "Wool Textile",
          weightGrams: 1560,
          percentageByWeight: 2.4,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        },
        {
          id: "MC-009",
          name: "Metal - Steel",
          materialType: "Metal - Steel",
          weightGrams: 650,
          percentageByWeight: 1.0,
          isRecycled: false,
          isPreConsumerRecycled: false,
          originCountry: "GB"
        }
      ],
      totalRecycledContentPercent: 12.5,
      preConsumerRecycledPercent: 12.5,
      postConsumerRecycledPercent: 0,
      bioBasedContentPercent: 0,
      renewableContentPercent: 0,
      verificationBody: "Bureau Veritas UK (planned)",
      verificationDate: "2026-01-15"
    },
    substancesOfConcern: [
      {
        id: "SOC-001",
        category: "CLP_HAZARDOUS",
        chemicalName: "4,4'-Methylenediphenyl diisocyanate (MDI)",
        commonName: "MDI",
        casNumber: "101-68-8",
        locationInProduct: "Polyurethane foam (cushion) — bound polymer, no free isocyanate at delivery",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "No free MDI at delivery; do not heat/thermally decompose cushion in service.",
        disposalInstructions: "Energy recovery via certified incineration; do not landfill bulk foam."
      },
      {
        id: "SOC-002",
        category: "SVHC",
        chemicalName: "Tris(2-chloroethyl) phosphate",
        commonName: "TCEP (flame retardant)",
        casNumber: "115-96-8",
        locationInProduct: "Foam (legacy formulations) — replaced with non-SVHC retardant from 2024",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses non-SVHC retardant. Legacy stock identified by batch < 2024-04."
      },
      {
        id: "SOC-003",
        category: "CIRCULARITY_INHIBITOR",
        chemicalName: "Lithium-ion electrolyte (LiPF6)",
        commonName: "Li-ion electrolyte",
        casNumber: "21324-40-3",
        locationInProduct: "IFE seat-power backup cell (sa5 electronics & power)",
        concentrationPercent: 1.2,
        concentrationUnit: "% of total seat mass",
        safeHandlingInstructions: "Do not puncture, crush, or expose to >60°C. Follow manufacturer guidance.",
        disposalInstructions: "Remove cell intact, route to certified Li-ion battery recycler."
      },
      {
        id: "SOC-004",
        category: "CLP_HAZARDOUS",
        chemicalName: "Chromium(VI) (legacy conversion coatings)",
        commonName: "Hexavalent chromium",
        casNumber: "18540-29-9",
        locationInProduct: "Aluminium structure surface treatment (legacy — phased out)",
        concentrationPercent: 0,
        concentrationUnit: "%",
        safeHandlingInstructions: "Current production uses Cr(VI)-free alternative (TCP). Reach-compliant."
      }
    ],
    carbonFootprintData: {
      totalKgCO2e: 1100.0,
      functionalUnit: "per seat-place over 15-year service life",
      phases: [
        {
          phase: "RAW_MATERIAL_ACQUISITION",
          kgCO2e: 374.0,
          percentageOfTotal: 34.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "MANUFACTURING",
          kgCO2e: 242.0,
          percentageOfTotal: 22.0,
          dataQuality: "PRIMARY"
        },
        {
          phase: "DISTRIBUTION",
          kgCO2e: 55.0,
          percentageOfTotal: 5.0,
          dataQuality: "SECONDARY"
        },
        {
          phase: "USE_PHASE",
          kgCO2e: 308.0,
          percentageOfTotal: 28.0,
          dataQuality: "ESTIMATED"
        },
        {
          phase: "END_OF_LIFE",
          kgCO2e: 121.0,
          percentageOfTotal: 11.0,
          dataQuality: "SECONDARY"
        }
      ],
      calculationMethodology: "ISO 14067:2018 (Thompson Aero internal LCA, programme estimate)",
      systemBoundary: "Cradle-to-grave",
      verificationBody: "Bureau Veritas UK (planned 2026)",
      verificationDate: "2026-06-30",
      dataQualityRating: 3
    },
    certificationsStructured: [
      {
        id: "CERT-001",
        type: "OTHER",
        name: "TSO-C127c",
        certificateNumber: "TSO-C127c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Whole-seat dynamic approval per SAE AS8049E (May 2025)"
      },
      {
        id: "CERT-002",
        type: "OTHER",
        name: "ETSO-C127c",
        certificateNumber: "ETSO-C127c",
        issuingBody: "EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "EASA equivalent"
      },
      {
        id: "CERT-003",
        type: "OTHER",
        name: "TSO-C39c",
        certificateNumber: "TSO-C39c",
        issuingBody: "FAA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Legacy 9g static — applies to non-dynamic load paths"
      },
      {
        id: "CERT-004",
        type: "OTHER",
        name: "FAR/CS 25.561",
        certificateNumber: "FAR/CS 25.561",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Emergency landing inertia (structural)"
      },
      {
        id: "CERT-005",
        type: "OTHER",
        name: "FAR/CS 25.562",
        certificateNumber: "FAR/CS 25.562",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Dynamic 16g test; HIC ≤ 1000"
      },
      {
        id: "CERT-006",
        type: "OTHER",
        name: "FAR/CS 25.785",
        certificateNumber: "FAR/CS 25.785",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Seats, safety belts and harnesses (top-level seat rule)"
      },
      {
        id: "CERT-007",
        type: "OTHER",
        name: "FAR/CS 25.789",
        certificateNumber: "FAR/CS 25.789",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Retention of items of mass (stowage / table)"
      },
      {
        id: "CERT-008",
        type: "OTHER",
        name: "FAR/CS 25.853(a)",
        certificateNumber: "FAR/CS 25.853(a)",
        issuingBody: "FAA / EASA",
        issueDate: "2025-06-01",
        expiryDate: "2030-06-01",
        scope: "Vertical Bunsen flammability (12-sec)"
      }
    ],
    supplyChain: {
      nodes: [
        {
          id: "SC-001",
          tier: 1,
          operator: {
            id: "OP-T1",
            role: "MANUFACTURER",
            name: "Thompson Aero Seating Ltd",
            address: "Sheepwalk Road",
            city: "Portadown, Co. Armagh",
            country: "United Kingdom",
            countryCode: "GB",
            registrationNumber: "NI032654",
            website: "https://www.thompsonaero.com"
          },
          componentSupplied: "Complete passenger suite (final assembly & integration)",
          percentageOfProduct: 100,
          dueDiligenceStatus: "VERIFIED",
          dueDiligenceDate: "2026-02-15",
          auditResult: "COMPLIANT",
          socialAuditCompleted: true,
          environmentalAuditCompleted: true
        },
        {
          id: "SC-002",
          tier: 2,
          operator: {
            id: "OP-T2-2",
            role: "MANUFACTURER",
            name: "Constellium",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Spreader machining (LH/RH, staggered geometry)",
          materialProvided: [
            "7075-T7351 aluminium plate, AMS-QQ-A-250/12 / AMS 4045"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Vantage staggered geometry has been in production since 2008 — the spreader is the most mature load path in Thompson's portfolio."
        },
        {
          id: "SC-003",
          tier: 2,
          operator: {
            id: "OP-T2-3",
            role: "MANUFACTURER",
            name: "Thompson Aero in-house",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat pan sub-frame",
          materialProvided: [
            "Welded 6061-T6 / machined 7075 alloy plate, with aluminium honeycomb sandwich panel (Hexcel ECA / CR III aluminium core typical for seat pans)"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. GUESS — Thompson does primary-structure machining in-house at Portadown."
        },
        {
          id: "SC-004",
          tier: 2,
          operator: {
            id: "OP-T2-4",
            role: "MANUFACTURER",
            name: "Aubert & Duval or ATI",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Track-attach feet (per-seat, structural)",
          materialProvided: [
            "15-5PH stainless or Ti-6Al-4V forgings to AMS 4928"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Aubert & Duval and ATI are category-dominant aerospace forgers."
        },
        {
          id: "SC-005",
          tier: 2,
          operator: {
            id: "OP-T2-5",
            role: "MANUFACTURER",
            name: "Lisi Aerospace / Howmet",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Hi-Lok / Hi-Lite pins & NAS bolts",
          materialProvided: [
            "15-5PH, A286 or Ti fasteners to NAS/MS spec"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Industry-standard aerospace fastener houses."
        },
        {
          id: "SC-006",
          tier: 2,
          operator: {
            id: "OP-T2-6",
            role: "MANUFACTURER",
            name: "Greiner aerospace",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Seat-pan cushion (multi-layer)",
          materialProvided: [
            "CMHR polyurethane stack: comfort layer ~40 kg/m³, support layer ~60 kg/m³, aramid felt fire blocker"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. ESTIMATED. Greiner aerospace is category-dominant; no Thompson tie publicly attested."
        },
        {
          id: "SC-007",
          tier: 2,
          operator: {
            id: "OP-T2-7",
            role: "MANUFACTURER",
            name: "Polymer Dynamics or Tex Tech Industries",
            address: "Address withheld (supplier confidence: PROBABLE)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Cushion fire-blocking layer",
          materialProvided: [
            "Meta-aramid felt; equivalent to legacy Vonar 3"
          ],
          dueDiligenceStatus: "PENDING",
          auditResult: "CONDITIONAL",
          notes: "Confidence: PROBABLE. Required for FAR 25.853(c) compliance."
        },
        {
          id: "SC-008",
          tier: 2,
          operator: {
            id: "OP-T2-8",
            role: "MANUFACTURER",
            name: "Andrew Muirhead & Son",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — leather (headrest, arm caps, console top)",
          materialProvided: [
            "Aviation-grade chrome-tanned leather, FR treatment per FAR 25.853"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Verified via Thompson's own sustainability page naming Muirhead. The cream leather headrest visible in the photo evidence is consistent with Muirhead."
        },
        {
          id: "SC-009",
          tier: 2,
          operator: {
            id: "OP-T2-9",
            role: "MANUFACTURER",
            name: "Replin by Hainsworth",
            address: "Address withheld (supplier confidence: VERIFIED)",
            city: "—",
            country: "—",
            countryCode: "XX"
          },
          componentSupplied: "Dress cover — woven wool / hemp (backrest face)",
          materialProvided: [
            "Undyed wool / alpaca / hemp blend, FR scrim backing; light grey channelled weave visible on the showroom seat"
          ],
          dueDiligenceStatus: "VERIFIED",
          auditResult: "COMPLIANT",
          notes: "Confidence: VERIFIED. Verified via Thompson sustainability page."
        }
      ],
      maxTierVisibility: 2,
      conflictMineralsFree: true,
      deforestationFree: true,
      forcedLaborFree: true,
      lastFullAuditDate: "2026-02-15",
      thirdPartyVerifierName: "Bureau Veritas UK"
    },
    endOfLifeData: {
      disassemblyInstructions: [
        {
          stepNumber: 1,
          instruction: "Disconnect 28V DC and seat-power harness at PSCU; isolate IFE Cat-6/HDMI.",
          toolsRequired: [
            "Insulated 6mm flat",
            "Wago lever-nut tool"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Electrical harness + PSCU",
          materialType: "Copper / Electronics",
          recyclingStream: "WEEE — electronics & cable",
          warningNotes: "Lock-out tag-out before unplugging."
        },
        {
          stepNumber: 2,
          instruction: "Remove IFE 24-inch OLED monitor and pivot arm.",
          toolsRequired: [
            "Torx T20",
            "Hex 4mm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "IFE display",
          materialType: "Electronics + Glass",
          recyclingStream: "WEEE — display"
        },
        {
          stepNumber: 3,
          instruction: "Unbolt privacy door and shroud assembly.",
          toolsRequired: [
            "Torx T30"
          ],
          estimatedTimeMinutes: 25,
          componentRemoved: "Privacy door + shroud",
          materialType: "Carbon Fibre Composite",
          recyclingStream: "CFRP recovery (pyrolysis or mechanical)"
        },
        {
          stepNumber: 4,
          instruction: "Remove cushion stack and dress covers; segregate leather from foam.",
          toolsRequired: [
            "Hand tools"
          ],
          estimatedTimeMinutes: 12,
          componentRemoved: "Cushion stack + dress cover",
          materialType: "PU foam + Leather/Wool",
          recyclingStream: "Energy recovery (foam); leather to specialist re-use"
        },
        {
          stepNumber: 5,
          instruction: "Remove actuators, gas struts and harness loom.",
          toolsRequired: [
            "Spanner 13/17mm",
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 20,
          componentRemoved: "Actuation assembly",
          materialType: "Electronics + Steel + Aluminium",
          recyclingStream: "WEEE + metal separation"
        },
        {
          stepNumber: 6,
          instruction: "Remove tray table and stowage compartments.",
          toolsRequired: [
            "Torx T25"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Tray + stowage",
          materialType: "Aluminium / Thermoplastic",
          recyclingStream: "Metal + plastic streams"
        },
        {
          stepNumber: 7,
          instruction: "Detach restraint (lap belt / inflatable belt assembly) for separate disposal.",
          toolsRequired: [
            "Hex 5mm"
          ],
          estimatedTimeMinutes: 8,
          componentRemoved: "Restraint assembly",
          materialType: "Textile + Metal",
          recyclingStream: "Manufacturer take-back (Schroth / AmSafe)",
          warningNotes: "If inflatable belt: depressurise and discharge initiator BEFORE removal."
        },
        {
          stepNumber: 8,
          instruction: "Lift trim & finish panels off the primary structure.",
          toolsRequired: [
            "Trim tools"
          ],
          estimatedTimeMinutes: 18,
          componentRemoved: "Trim panels",
          materialType: "Composite + Phenolic",
          recyclingStream: "Composite recovery / energy"
        },
        {
          stepNumber: 9,
          instruction: "Unbolt seat from seat track and lift primary structure.",
          toolsRequired: [
            "Torque wrench 30Nm"
          ],
          estimatedTimeMinutes: 15,
          componentRemoved: "Primary structure (spreader/beam/shell)",
          materialType: "Aluminium + CFRP",
          recyclingStream: "Aluminium closed-loop + CFRP recovery"
        }
      ],
      totalDisassemblyTimeMinutes: 148,
      nonDestructiveRemovalPossible: true,
      specialToolsRequired: [
        "Torque wrench 30Nm",
        "Insulated 6mm flat",
        "Trim tools"
      ],
      professionalDisassemblyRequired: true,
      recyclableMaterialsPercent: 78,
      recoverableEnergyPercent: 14,
      landfillPercent: 4,
      hazardousWastePercent: 4,
      recyclingFacilityRequirements: "Aerospace-certified processor with WEEE handling, Li-ion battery containment, and CFRP recovery capability.",
      hazardousComponentsWarning: "Lithium-ion seat backup cell and (if fitted) pyrotechnic restraint initiator must be removed by trained personnel.",
      takeBackSchemeAvailable: true,
      takeBackSchemeUrl: "https://www.thompsonaero.com/sustainability/take-back",
      takeBackSchemePhone: "+44 28 3839 8888"
    },
    dppVersion: "1.0",
    dppSchemaVersion: "ESPR-2026",
    dppComplianceStatus: "COMPLIANT",
    dppLastUpdate: "2026-05-21T10:00:00.000Z",
    dataQualityScore: 76,
    blockchainTxId: "0xvantage001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    blockchainHistory: [
      {
        txId: "0xvantage001cafebabeaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
        timestamp: "2024-04-12T10:00:00.000Z",
        action: "CREATE"
      }
    ],
    createdAt: "2024-04-12T10:00:00.000Z",
    updatedAt: "2026-05-21T10:00:00.000Z"
  }
];
